import { BenefitTypeOptions } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { checkBenefitForMember } from 'api/scopeAssessorAPI';
import { handleError } from 'services/Error';
import { Sections } from 'services/testReferralHelpers';
import {
    useGetAllSuppliersQuery,
    useGetPayerSegmentsQuery,
    useGetPlacesOfServiceQuery,
} from 'store/api/referenceApi';

export const PatientAndPharmacySection = ({
    resetProductAndResults,
    setShowPatientDialog,
    setLoadingText,
}) => {
    const formik = useFormikContext();

    const { data: allSuppliersData, isFetching: suppliersFetching } = useGetAllSuppliersQuery();
    const { supplierList } = allSuppliersData ?? {};

    const { data: segments, isFetching: segmentsFetching } = useGetPayerSegmentsQuery();
    const { payerSegmentList } = segments ?? {};

    const { data: placesOfService, isFetching: placesOfServiceFetching } =
        useGetPlacesOfServiceQuery();

    const { eligibility: _eligibility, ...patientAndPharmErrors } =
        getIn(formik.errors, Sections.PATIENT_AND_PHARMACY) ?? {};

    const checkPatientBenefit = async ({
        healthPlan,
        patientFirstName: firstName,
        patientLastName: lastName,
        memberDOB: dateOfBirth,
        cardholderId,
    }) => {
        try {
            const benefitCheck = await checkBenefitForMember({
                payerId: healthPlan?.payerId,
                segmentId: healthPlan?.secondaryData,
                firstName,
                lastName,
                dateOfBirth,
                cardholderId,
            });
            await formik.setFieldValue(
                `${Sections.PATIENT_AND_PHARMACY}.eligibility`,
                benefitCheck,
            );
            await resetProductAndResults();
            await formik.validateForm();
            setShowPatientDialog(true);
        } catch (err) {
            handleError("Can't retrieve patient benefit check results.", err);
        }
    };

    return (
        <>
            <RadioButtonGroupFormik
                name={`${Sections.PATIENT_AND_PHARMACY}.includePendStart`}
                label="Include Pending Start Route Rules?"
                helperText="These are configured route rules that have a start date in the future and are not in production."
                required
                options={['No', 'Yes']}
                selectedValue={
                    formik.values[Sections.PATIENT_AND_PHARMACY].includePendStart ? 'Yes' : 'No'
                }
                onChange={(newVal) =>
                    formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.includePendStart`,
                        newVal === 'Yes',
                    )
                }
            />
            <SuggestSingleSelectFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.pharmacy`}
                label="Pharmacy"
                helperText="Selecting a pharmacy is optional. If selected, the test referral will run from the perspective of that pharmacy."
                placeholderText="Select Pharmacy"
                values={supplierList}
                isLoading={suppliersFetching}
                onChange={async (newValue) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.pharmacy`,
                        newValue,
                    );
                    await resetProductAndResults();
                }}
            />
            <SuggestSingleSelectFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.healthPlan`}
                label="Health Plan"
                placeholderText="Select Patient Plan"
                values={payerSegmentList}
                isLoading={segmentsFetching}
                onChange={async (newValue) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.healthPlan`,
                        newValue,
                    );
                    await resetProductAndResults();
                }}
                required
            />
            <TextInputFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.patientFirstName`}
                label="Patient First Name"
                placeholderText="Enter Patient First Name"
                onChange={async (e) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.patientFirstName`,
                        e.target.value,
                    );
                    await resetProductAndResults();
                }}
                required
            />
            <TextInputFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.patientLastName`}
                label="Patient Last Name"
                placeholderText="Enter Patient Last Name"
                onChange={async (e) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.patientLastName`,
                        e.target.value,
                    );
                    await resetProductAndResults();
                }}
            />
            <TextInputFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.cardholderId`}
                label="Cardholder ID"
                placeholderText="Enter Cardholder ID"
                onChange={async (e) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.cardholderId`,
                        e.target.value,
                    );
                    await resetProductAndResults();
                }}
                required
            />
            <DateInputFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.memberDOB`}
                required
                hideCalendarPopover
                label="Member Date of Birth"
                onChange={async (newValue) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.memberDOB`,
                        formatDate(newValue, DateFormats.HYPHEN_DATE),
                    );
                    await resetProductAndResults();
                }}
            />
            <TextInputFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.prescriber`}
                label="Prescriber"
                placeholderText="Enter Prescriber"
                onChange={async (e) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.prescriber`,
                        e.target.value,
                    );
                    await resetProductAndResults();
                }}
                required
            />
            <SuggestSingleSelectFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.benefit`}
                label="Benefit"
                helperText="Select which benefit to use for determining available products."
                placeholderText="Select Benefit"
                values={BenefitTypeOptions}
                onChange={async (newValue) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.benefit`,
                        newValue,
                    );
                    await resetProductAndResults();
                }}
                required
            />
            <SuggestSingleSelectFormik
                id={`${Sections.PATIENT_AND_PHARMACY}.placeOfService`}
                label="Place of Service"
                placeholderText="Select Place of Service"
                isLoading={placesOfServiceFetching}
                values={placesOfService}
                onChange={async (newValue) => {
                    await formik.setFieldValue(
                        `${Sections.PATIENT_AND_PHARMACY}.placeOfService`,
                        newValue,
                    );
                    await resetProductAndResults();
                }}
                required
            />
            <Button
                intent={Button.Intent.PRIMARY}
                disabled={Object.keys(patientAndPharmErrors).length > 0}
                label="Check Patient Benefits"
                onClick={async () => {
                    setLoadingText('Checking Patient Benefits');
                    await checkPatientBenefit(getIn(formik.values, Sections.PATIENT_AND_PHARMACY));
                    setLoadingText();
                }}
            />
        </>
    );
};

PatientAndPharmacySection.propTypes = {
    resetProductAndResults: PropTypes.func.isRequired,
    setShowPatientDialog: PropTypes.func.isRequired,
    setLoadingText: PropTypes.func.isRequired,
};
